// Visão geral — responde "onde está o gargalo agora?" sem clique.
//
// Não existia. O curador entrava em Camadas e precisava abrir card a card para
// descobrir o que estava travado. As três razões pelas quais uma publicação
// não sai — fonte sem licença, camada que parou de executar, execução que
// falhou — agora estão num lugar só.
//
// Os totais vêm de `GET /api/painel`, agregados no banco. Compor no cliente a
// partir das outras rotas somaria só o que coube na página, e mentiria.
//
// ISTO NÃO É UM FUNIL, e deixou de se desenhar como um.
//
// Os quatro cartões estavam lado a lado com uma barra de proporção cada, o que
// se lê de cima para baixo como afunilamento: cada estágio sendo o que sobrou do
// anterior. A leitura é errada, e não por causa de um número errado — por causa
// do que os números são:
//
//   - `aguardando`, `validado` e `rejeitado` PARTICIONAM o staging. Uma feição
//     está em exatamente um estado. Validar tudo deixa `aguardando` em zero e
//     `validado` no total: o "estágio" CRESCE, e esse é o estado saudável.
//   - `publicado` vem de outra tabela e pode passar `ingerido`, porque
//     `limpar_staging` apaga a curadoria e a versão selada continua no ar.
//
// Ou seja: a garantia de "cada estágio menor ou igual ao anterior" não existe
// nos dados, e não é possível fazê-la existir sem inventar número. Então o
// componente deixou de ser funil. São quatro cartões independentes, e a única
// barra da tela mostra a coisa que É uma proporção verdadeira: como o staging se
// divide entre os três status, que somam o topo por invariante de schema.

import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { obter, type Painel as Dados } from "../api.js";
import { Carregando, Falha, quando } from "../pecas/basicos.js";

/** "1 coleções no ar" é o tipo de detalhe que faz a tela parecer protótipo. */
const plural = (n: number, um: string, varios: string): string => `${n} ${n === 1 ? um : varios}`;

/**
 * Fração para leitura, com o caso de borda que engana.
 *
 * `0%` para um valor não-zero é pior que não mostrar nada: numa camada de 300 mil
 * feições, 400 rejeitadas arredondam para 0% e o curador conclui que não há
 * rejeição nenhuma. `<1%` diz a mesma coisa sem a afirmação falsa.
 */
function fracao(n: number, total: number): string {
  if (total === 0 || n === 0) return "0%";
  const p = (n / total) * 100;
  return p < 0.5 ? "<1%" : `${Math.round(p)}%`;
}

const ACAO: Record<string, string> = {
  validar_lote: "validou em lote",
  promover_versao: "promoveu versão",
  publicar_colecao: "publicou coleção",
  upload_arquivo: "subiu arquivo",
  limpar_staging: "limpou staging",
  alterar_agendamento: "alterou agendamento",
};

export function PainelGeral({
  aoIrPara,
  aoAbrirCamada,
}: {
  aoIrPara: (tela: string) => void;
  aoAbrirCamada: (id: string) => void;
}): ReactNode {
  const p = useQuery({ queryKey: ["painel"], queryFn: () => obter<Dados>("/painel") });
  if (p.error) return <Falha erro={p.error} />;
  // O DADO, não o status — e o `!` que estava aqui era a licença para o erro.
  //
  // Medido em produção: `TypeError: Cannot read properties of undefined
  // (reading 'fila')`, e a tela inteira em branco. `isLoading` é falso enquanto
  // `data` ainda é `undefined` — depois de `clear()` no logout, de um
  // `invalidate` que refaz tudo, ou de uma consulta que ainda não disparou.
  //
  // Não é a tela que cai: é o APP. Sem fronteira de erro, uma exceção em
  // render desmonta a árvore toda, incluindo a barra lateral e o "Sair". A
  // fronteira entrou junto (`App.tsx`), mas a correção certa é não lançar.
  //
  // `== null` cobre `undefined` E `null`. Era `=== undefined`, e um corpo
  // `null` na resposta passava pela guarda para morrer em `d.funil` na linha
  // seguinte — o mesmo `TypeError`, com a guarda no lugar.
  const d = p.data;
  if (d == null || d.funil == null) return <Carregando />;

  const f = d.funil;
  // A partição, conferida no cliente. O servidor garante a soma por invariante
  // de schema (três status e só três), mas quem desenha a barra não pode SUPOR
  // isso: se um status novo aparecer sem esta tela saber, a barra ficaria curta
  // e ninguém saberia por quê. Divergência vira nota visível, não silêncio.
  const emStaging = f.aguardando + f.validado + f.rejeitado;
  const fechaAParticao = emStaging === f.ingerido;
  // O único par que pode inverter, e o motivo tem nome.
  const stagingLimpo = f.publicado > f.ingerido;

  // As cores são as mesmas das pílulas e do mapa: aguardando, validado e
  // rejeitado já têm cor no projeto, e inventar outra aqui faria o curador
  // aprender duas.
  const partes = [
    ["aguardando", f.aguardando, "var(--agd)"],
    ["validado", f.validado, "var(--val)"],
    ["rejeitado", f.rejeitado, "var(--err)"],
  ] as const;

  const maiorFila = Math.max(1, ...d.fila.map((x) => x.aguardando));

  return (
    <div className="faixas">
      {/* Quatro cartões INDEPENDENTES, sem barra de proporção em cada um: com
          uma barra por cartão a fileira se lê como afunilamento, e o
          afunilamento não existe (ver o topo do arquivo). O que substitui a
          barra é a divisão do staging, logo abaixo — que é proporção de
          verdade. */}
      <div className="grade-numeros">
        <CartaoNumero
          rotulo="Ingerido"
          cor="var(--tinta2)"
          n={f.ingerido}
          nota={`${plural(f.camadas, "camada", "camadas")}, ${plural(f.fontes, "fonte", "fontes")}`}
        />
        <CartaoNumero
          rotulo="Aguardando"
          cor="var(--agd)"
          n={f.aguardando}
          nota={plural(f.camadas_com_fila, "camada com fila", "camadas com fila")}
        />
        <CartaoNumero
          rotulo="Validado"
          cor="var(--val)"
          n={f.validado}
          // DOIS números, não uma soma. A linha de curadoria continua existindo
          // depois de promovida, então somá-la à feição de versão contava a
          // mesma coisa duas vezes — e a tela mostrava um número maior que o
          // total ingerido. Cada um responde a sua própria pergunta.
          nota={`${f.em_versoes.toLocaleString("pt-BR")} em versões seladas`}
        />
        <CartaoNumero
          rotulo="Publicado"
          cor="var(--pub)"
          n={f.publicado}
          nota={plural(f.colecoes_no_ar, "coleção no ar", "coleções no ar")}
        />
      </div>

      <section className="painel">
        <div className="painel-topo">
          <h2>Como o staging está dividido</h2>
        </div>
        {f.ingerido === 0 ? (
          <p className="vazio">Nada em staging.</p>
        ) : (
          <>
            {/* A ÚNICA barra proporcional da tela, e a única que pode ser: os
                três status somam o ingerido por construção, então cada faixa é
                uma fração real de um todo — não um estágio comparado com o
                anterior. */}
            <div className="barra-particao">
              {partes
                .filter(([, n]) => n > 0)
                .map(([nome, n, cor]) => (
                  <div
                    key={nome}
                    style={{ width: `${(n / f.ingerido) * 100}%`, background: cor }}
                    title={`${nome}: ${n.toLocaleString("pt-BR")}`}
                  />
                ))}
            </div>
            {/* A legenda traz a FRAÇÃO, não a contagem: a contagem está no
                cartão logo acima, e repeti-la a dois centímetros de distância só
                dá ao curador duas coisas para conferir uma contra a outra. */}
            <div className="legenda-particao">
              {partes.map(([nome, n, cor]) => (
                <span key={nome}>
                  <i style={{ background: cor }} />
                  {nome} <b className="mono">{fracao(n, f.ingerido)}</b>
                </span>
              ))}
            </div>
            <p className="nota">
              Os três somam o ingerido: uma feição está em um estado só. Validar tudo leva
              aguardando a zero — não é o número caindo, é a fila esvaziando.
            </p>
            {!fechaAParticao ? (
              <div className="aviso aviso-erro" role="status">
                <div>
                  <b>A divisão não fecha com o total ingerido</b>
                  <p>
                    {emStaging.toLocaleString("pt-BR")} somados contra{" "}
                    {f.ingerido.toLocaleString("pt-BR")} ingeridos. Há status em staging que esta
                    tela não conhece — a barra está incompleta, e os números dos cartões continuam
                    corretos.
                  </p>
                </div>
              </div>
            ) : null}
          </>
        )}
        {stagingLimpo ? (
          <div className="aviso" role="status" style={{ marginTop: 12 }}>
            <div>
              <b>Há mais feição publicada que em staging</b>
              <p>
                {f.publicado.toLocaleString("pt-BR")} publicadas contra{" "}
                {f.ingerido.toLocaleString("pt-BR")} em staging. É o efeito de limpar o staging: a
                versão selada continua no ar, porque versão publicada é imutável. Republicar exige
                ingerir de novo.
              </p>
            </div>
          </div>
        ) : null}
      </section>

      <div className="grade-paineis">
        <section className="painel">
          <div className="painel-topo">
            <h2>O que trava a publicação</h2>
            <button className="link-texto" onClick={() => aoIrPara("fontes")}>
              Fontes →
            </button>
          </div>
          <div className="pilha">
            <Trava
              titulo="Fontes sem licença ou atribuição"
              detalhe="Curável, não publicável — a trava é coluna gerada no banco."
              n={d.travas.fontes_sem_licenca}
              cor="var(--agd)"
            />
            <Trava
              titulo="Camadas sem executar no prazo da cadência"
              detalhe='Silêncio se parece com "está tudo bem".'
              n={d.travas.camadas_sem_executar}
              cor="var(--agd)"
            />
            <Trava
              titulo="Última execução falhou"
              detalhe="A camada tem dado velho e ninguém foi avisado."
              n={d.travas.execucoes_com_erro}
              cor="var(--err)"
              erro
            />
          </div>
        </section>

        <section className="painel">
          <div className="painel-topo">
            <h2>Fila de curadoria</h2>
            <button className="link-texto" onClick={() => aoIrPara("camadas")}>
              Camadas →
            </button>
          </div>
          {d.fila.length === 0 ? (
            <p className="vazio">Nenhuma feição esperando validação.</p>
          ) : (
            <div>
              {d.fila.map((x) => (
                <button key={x.id} className="linha-fila" onClick={() => aoAbrirCamada(x.id)}>
                  <span style={{ minWidth: 0 }}>
                    <span className="fila-nome">{x.nome}</span>
                    <span className="fila-sub" style={{ display: "block" }}>
                      {x.fonte} · {x.conector}
                    </span>
                  </span>
                  {/* Aqui a barra é comparação entre as camadas da própria
                      lista — o denominador é a maior fila, não um estágio. */}
                  <span className="barra-fila">
                    <div style={{ width: `${(x.aguardando / maiorFila) * 100}%` }} />
                  </span>
                  <span className="fila-n">{x.aguardando.toLocaleString("pt-BR")}</span>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>

      <div className="grade-paineis">
        <section className="painel">
          <div className="painel-topo">
            <h2>Coleções no ar</h2>
            <button className="link-texto" onClick={() => aoIrPara("colecoes")}>
              Coleções →
            </button>
          </div>
          {d.colecoes.length === 0 ? (
            <p className="vazio">Nada publicado ainda.</p>
          ) : (
            <ul className="lista-simples" style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {d.colecoes.map((c) => (
                <li key={c.id_publico}>
                  <span className="ponto-pub" />
                  <span
                    style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}
                    title={c.id_publico}
                  >
                    {c.id_publico}
                  </span>
                  <span className="terciario">v{c.versao ?? "—"}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="painel">
          <div className="painel-topo">
            <h2>Últimas ações</h2>
            <button className="link-texto" onClick={() => aoIrPara("auditoria")}>
              Auditoria →
            </button>
          </div>
          {d.auditoria.length === 0 ? (
            <p className="vazio">Nada registrado ainda.</p>
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {d.auditoria.map((a, i) => (
                <li
                  key={`${a.criado_em}-${i}`}
                  style={{ display: "flex", gap: 10, alignItems: "baseline", padding: "4px 0" }}
                >
                  <span
                    className="mono terciario"
                    style={{ width: 76, flex: "none", fontSize: 10.5 }}
                  >
                    {new Date(a.criado_em).toLocaleString("pt-BR", {
                      day: "2-digit",
                      month: "2-digit",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  <span style={{ fontSize: 12 }}>
                    <b style={{ fontWeight: 600 }}>{a.ator}</b>{" "}
                    <span style={{ color: "var(--tinta2)" }}>{ACAO[a.acao] ?? a.acao}</span>{" "}
                    <span className="mono terciario" style={{ fontSize: 11 }}>
                      {a.alvo ?? ""}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

/**
 * Um número, com o que ele significa embaixo.
 *
 * Era `CartaoFunil` e tinha uma barra de proporção. A barra saiu: quatro delas
 * em fileira se leem como afunilamento, e os quatro números não formam um.
 * A cor de cada cartão é a mesma do status na barra de partição e nas pílulas,
 * que é o que ainda liga a fileira ao ciclo sem afirmar uma ordem de grandeza.
 */
function CartaoNumero({
  rotulo,
  cor,
  n,
  nota,
}: {
  rotulo: string;
  cor: string;
  n: number;
  nota: string;
}): ReactNode {
  return (
    <div className="cartao-numero" style={{ ["--cor-estagio" as string]: cor }}>
      <div className="rotulo-estagio">{rotulo}</div>
      <div className="numero-grande">{n.toLocaleString("pt-BR")}</div>
      <p className="nota">{nota}</p>
    </div>
  );
}

function Trava({
  titulo,
  detalhe,
  n,
  cor,
  erro,
}: {
  titulo: string;
  detalhe: string;
  n: number;
  cor: string;
  erro?: boolean;
}): ReactNode {
  return (
    <div className={`aviso ${erro ? "aviso-erro" : ""} ${n === 0 ? "aviso-ok" : ""}`}>
      <div style={{ minWidth: 0 }}>
        <b>{titulo}</b>
        <p>{detalhe}</p>
      </div>
      <span className="aviso-n" style={{ color: n === 0 ? "var(--pub)" : cor }}>
        {n}
      </span>
    </div>
  );
}

export { quando };
